import { assign } from 'lodash';
import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl } from '@angular/forms';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import { ZodSharedValidator } from '@medsurf/flat-validators';

@Component({
    selector: 'medsurf-slide-edit-shared-text-layout-form',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './shared-text-layout-form.component.html',
    styleUrls: ['./shared-text-layout-form.component.scss'],
    standalone: false
})
export class SharedTextLayoutFormComponent {
  #isSubmitted = signal(false);
  #annotationLabel: FlatModels.AnnotationLabelEntityModels.AnnotationLabel | null = null;
  annotationLabelForm = new FormGroup(
    {
      text: new FormControl(''),
      align: new FormControl(''),
      pivot: new FormControl(''),
      color: new FormControl(''),
      fontSize: new FormControl(14),
      border: new FormControl(false),
      borderColor: new FormControl(''),
      borderWidth: new FormControl(1),
      description: new FormControl(''),
      hidden: new FormControl(false),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.AnnotationLabelEntityModels.AnnotationLabelSchema.pick({
          text: true,
          align: true,
          pivot: true,
          color: true,
          fontSize: true,
          border: true,
          borderColor: true,
          borderWidth: true,
          description: true,
          hidden: true,
        })
      ),
      updateOn: 'change'
    }
  );
  annotationLabelBackgroundForm= new FormGroup(
    {
      color: new FormControl(''),
      opacity: new FormControl(40),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.AnnotationLabelEntityModels.MarkerBackgroundSchema.pick({
          color: true,
          opacity: true,
        })
      ),
      updateOn: 'change'
    }
  );
  readonly FlatModels = FlatModels;

  constructor(public alertControlFacade: FlatFacades.AlertControlFacade,
              public slideAuthorFacade: FlatFacades.SlideAuthorFacade) {
    // Update Annotation Label
    this.slideAuthorFacade.currentSelectedSlideAnnotationLabel$
      .pipe(takeUntilDestroyed())
      .subscribe((annotationLabel) => {
        if (!annotationLabel) {
          this.#annotationLabel = null;
          this.annotationLabelForm.reset();
          this.annotationLabelBackgroundForm.reset();
          this.#isSubmitted.set(false);
          return;
        }

        this.#annotationLabel = structuredClone(annotationLabel);
        const { text, align, pivot, color, fontSize, border, borderColor, borderWidth, description, hidden } = annotationLabel;
        this.annotationLabelForm.reset({ text, align, pivot, color, fontSize, border, borderColor, borderWidth, description, hidden });
        const backgroundOpacity = annotationLabel?.background?.opacity != null ? annotationLabel.background.opacity * 100 : 40;
        this.annotationLabelBackgroundForm.reset({
          color: annotationLabel?.background?.color ?? '',
          opacity: backgroundOpacity,
        });
        this.#isSubmitted.set(false);
      });
  }

  onSubmit() {
    if (this.#isSubmitted() || this.annotationLabelForm.invalid || !this.#annotationLabel) {
      return;
    }
    this.#isSubmitted.set(true);
    
    // Update Annotation Label
    const { color: backgroundColor, opacity } = this.annotationLabelBackgroundForm.value;
    const normalizedOpacity = typeof opacity === 'number' ? opacity / 100 : 0;
    const annotationLabel = assign(
      this.#annotationLabel,
      this.annotationLabelForm.value,
      {
        background: {
          color: backgroundColor,
          opacity: normalizedOpacity,
        }
      }
    );
    this.slideAuthorFacade.requestUpdateAnnotationLabel(annotationLabel, 'history.page_update_description').subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.annotationLabelForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
      }
    });
  }
  
  onShowBackground() {
    if (this.annotationLabelBackgroundForm.value.opacity === 0) {
      this.annotationLabelBackgroundForm.patchValue({opacity: 40 });
    } else {
      this.annotationLabelBackgroundForm.patchValue({opacity: 0 });
    }
    if (!this.annotationLabelBackgroundForm.invalid) {
      this.onSubmit();
    }
  }
}
